import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useOutletContext, useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Fade,
  Paper,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import SupportAgentOutlinedIcon from "@mui/icons-material/SupportAgentOutlined";
import dayjs from "dayjs";
import { useNotification } from "../../componentes/notificationProvider";
import { styless } from "./styles";
import { useDemandas } from "./useDemandas";
import {
  AbaChamados,
  ComposerPayload,
  Demanda,
  Periodo,
  STATUS_ABERTAS,
  STATUS_LIST,
  StatusMeta,
} from "./types";
import DemandasTable from "../../componentes/suporte/DemandasTable";
import DemandaDrawer from "../../componentes/suporte/DemandaDrawer";
import FiltersBar from "../../componentes/suporte/FiltersBar";
import NovaDemandaModal from "../../componentes/suporte/NovaDemandaModal";
import ArquivarModal from "../../componentes/suporte/ArquivarModal";
import { EmptyState } from "../../componentes/suporte/shared";

const PERIODO_VAZIO: Periodo = { inicio: null, fim: null };

const ABAS = [
  { chave: "abertas" as const, rotulo: "Abertas" },
  { chave: "concluidas" as const, rotulo: "Concluídas" },
];

const VAZIO: Record<AbaChamados, { titulo: string; texto: string }> = {
  abertas: {
    titulo: "Nenhum chamado em aberto",
    texto:
      "Quando você abrir uma solicitação, ela aparece aqui com o andamento em tempo real.",
  },
  concluidas: {
    titulo: "Nenhum chamado concluído",
    texto: "Os chamados finalizados pela equipe ficam guardados nesta aba.",
  },
  arquivadas: {
    titulo: "Nenhum chamado arquivado",
    texto:
      "Arquive um chamado para tirá-lo das outras abas sem perder o histórico. Ele continua acessível por aqui.",
  },
};

function codigoDoParametro(valor: string | null): string | null {
  if (!valor) return null;
  const normalizado = valor.replace(/-/g, "").slice(0, 8).toUpperCase();
  return normalizado || null;
}

function useDebounce<T>(valor: T, atraso: number): T {
  const [adiado, setAdiado] = useState(valor);
  useEffect(() => {
    const timer = setTimeout(() => setAdiado(valor), atraso);
    return () => clearTimeout(timer);
  }, [valor, atraso]);
  return adiado;
}

export default function Suporte() {
  const theme = useTheme();
  const styles = styless(theme);
  const notify = useNotification();
  const outlet = useOutletContext<{ setTitlePage?: (t: string) => void }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const {
    demandas,
    loading,
    erro,
    aviso,
    online,
    somenteLeitura,
    sincronizadoEm,
    usuario,
    carregarDetalhe,
    criarDemanda,
    adicionarComentario,
    adicionarResposta,
    editarComentario,
    arquivarDemanda,
    ressincronizar,
  } = useDemandas();

  const [aba, setAba] = useState<AbaChamados>("abertas");
  const [busca, setBusca] = useState("");
  const [status, setStatus] = useState<StatusMeta[]>([]);
  const [periodo, setPeriodo] = useState<Periodo>(PERIODO_VAZIO);
  const [modalAberto, setModalAberto] = useState(false);
  const [sincronizando, setSincronizando] = useState(false);
  const [arquivandoId, setArquivandoId] = useState<string | null>(null);
  const [demandaParaArquivar, setDemandaParaArquivar] =
    useState<Demanda | null>(null);

  const codigoAberto = codigoDoParametro(searchParams.get("chamado"));

  const abrirDemanda = useCallback(
    (codigo: string) => {
      setSearchParams(
        (prev) => {
          const proximos = new URLSearchParams(prev);
          proximos.set("chamado", codigo);
          return proximos;
        },
        { replace: false },
      );
    },
    [setSearchParams],
  );

  const fecharDemanda = useCallback(() => {
    setSearchParams(
      (prev) => {
        const proximos = new URLSearchParams(prev);
        proximos.delete("chamado");
        return proximos;
      },
      { replace: false },
    );
  }, [setSearchParams]);

  const buscaAdiada = useDebounce(busca, 150);

  useEffect(() => {
    outlet?.setTitlePage?.("");
  }, [outlet]);

  const abertas = useMemo(
    () =>
      demandas.filter((demanda) => !demanda.concluida && !demanda.arquivada),
    [demandas],
  );
  const concluidas = useMemo(
    () => demandas.filter((demanda) => demanda.concluida && !demanda.arquivada),
    [demandas],
  );
  const arquivadas = useMemo(
    () => demandas.filter((demanda) => demanda.arquivada),
    [demandas],
  );

  const aplicarFiltros = useCallback(
    (lista: Demanda[]) => {
      const termo = buscaAdiada.trim().toLowerCase();
      const chaves = status.map((meta) => meta.key);
      return lista.filter((demanda) => {
        if (
          termo &&
          !`${demanda.codigo} ${demanda.titulo}`.toLowerCase().includes(termo)
        ) {
          return false;
        }
        if (chaves.length && !chaves.includes(demanda.status)) return false;
        if (periodo.inicio || periodo.fim) {
          const criada = dayjs(demanda.dataCriacao);
          if (!criada.isValid()) return false;
          if (periodo.inicio && criada.isBefore(dayjs(periodo.inicio), "day")) {
            return false;
          }
          if (periodo.fim && criada.isAfter(dayjs(periodo.fim), "day")) {
            return false;
          }
        }
        return true;
      });
    },
    [buscaAdiada, status, periodo],
  );

  const filtradasAbertas = useMemo(
    () => aplicarFiltros(abertas),
    [aplicarFiltros, abertas],
  );
  const filtradasConcluidas = useMemo(
    () => aplicarFiltros(concluidas),
    [aplicarFiltros, concluidas],
  );
  const filtradasArquivadas = useMemo(
    () => aplicarFiltros(arquivadas),
    [aplicarFiltros, arquivadas],
  );

  const totalPorAba: Record<AbaChamados, number> = {
    abertas: filtradasAbertas.length,
    concluidas: filtradasConcluidas.length,
    arquivadas: filtradasArquivadas.length,
  };

  const lista =
    aba === "abertas"
      ? filtradasAbertas
      : aba === "concluidas"
        ? filtradasConcluidas
        : filtradasArquivadas;

  const ativos =
    status.length + (periodo.inicio || periodo.fim ? 1 : 0) + (busca ? 1 : 0);

  const limparFiltros = () => {
    setBusca("");
    setStatus([]);
    setPeriodo(PERIODO_VAZIO);
  };

  const trocarAba = (proxima: AbaChamados) => {
    if (proxima === aba) return;
    setAba(proxima);
    setStatus([]);
  };

  const aberta = useMemo(
    () => demandas.find((demanda) => demanda.codigo === codigoAberto) ?? null,
    [demandas, codigoAberto],
  );

  // `notify` muda de identidade a cada notificação (notificationProvider não a
  // memoiza) — mantê-la fora das deps evita reexecutar o efeito, e por tabela
  // reenviar a mesma requisição, toda vez que uma notificação é exibida.
  const notifyRef = useRef(notify);
  useEffect(() => {
    notifyRef.current = notify;
  }, [notify]);

  useEffect(() => {
    if (!aberta || somenteLeitura) return;
    if (aberta.pendenteSync || aberta.detalheCarregado) return;

    carregarDetalhe(aberta.id).catch((falha: { mensagem?: string }) => {
      notifyRef.current(
        falha?.mensagem ?? "Não foi possível carregar a conversa.",
        "error",
        7000,
      );
    });
  }, [aberta, carregarDetalhe, somenteLeitura]);

  const handleTentarNovamente = (demandaId: string) => {
    carregarDetalhe(demandaId).catch((falha: { mensagem?: string }) => {
      notify(
        falha?.mensagem ?? "Não foi possível carregar a conversa.",
        "error",
        7000,
      );
    });
  };

  const handleSincronizar = async () => {
    setSincronizando(true);
    try {
      const falha = await ressincronizar();
      if (falha) notify(falha.mensagem, "error", 8000);
      else notify("Chamados sincronizados.", "success");
    } finally {
      setSincronizando(false);
    }
  };

  const handleArquivar = async (demanda: Demanda) => {
    const arquivar = !demanda.arquivada;
    setArquivandoId(demanda.id);
    try {
      await arquivarDemanda(demanda.id, arquivar);
      notify(
        arquivar
          ? `Chamado ${demanda.codigo} arquivado. Veja em Arquivadas.`
          : `Chamado ${demanda.codigo} restaurado.`,
        "success",
        5000,
      );
    } catch (falha) {
      const { mensagem } = falha as { mensagem?: string };
      notify(
        mensagem ??
          (arquivar
            ? "Não foi possível arquivar o chamado."
            : "Não foi possível restaurar o chamado."),
        "error",
        9000,
      );
    } finally {
      setArquivandoId(null);
    }
  };

  const confirmarArquivar = () => {
    if (!demandaParaArquivar) return;
    const alvo = demandaParaArquivar;
    setDemandaParaArquivar(null);
    handleArquivar(alvo);
  };

  const handleCriar = async (titulo: string, descricao: ComposerPayload) => {
    try {
      const { demanda, aviso: avisoCriacao } = await criarDemanda(
        titulo,
        descricao,
      );
      setAba("abertas");
      limparFiltros();

      if (avisoCriacao) notify(avisoCriacao, "warning", 4000);
      else
        notify(
          `Chamado ${demanda.codigo} aberto com sucesso.`,
          "success",
          4000,
        );
    } catch (falha) {
      const { mensagem } = falha as { mensagem?: string };
      notify(mensagem ?? "Não foi possível abrir o chamado.", "error", 9000);
      throw falha;
    }
  };

  const handleComentar = async (payload: ComposerPayload) => {
    if (!aberta) return;
    try {
      await adicionarComentario(aberta.id, payload);
      notify("Mensagem enviada.", "success");
    } catch (falha) {
      const { mensagem } = falha as { mensagem?: string };
      notify(mensagem ?? "Não foi possível enviar a mensagem.", "error", 9000);
      throw falha;
    }
  };

  const handleResponder = async (
    comentarioId: string,
    payload: ComposerPayload,
  ) => {
    if (!aberta) return;
    try {
      await adicionarResposta(aberta.id, comentarioId, payload);
      notify("Resposta enviada.", "success");
    } catch (falha) {
      const { mensagem } = falha as { mensagem?: string };
      notify(mensagem ?? "Não foi possível enviar a resposta.", "error", 9000);
      throw falha;
    }
  };

  const handleEditar = async (
    comentarioId: string,
    payload: ComposerPayload,
  ) => {
    if (!aberta) return;
    try {
      await editarComentario(aberta.id, comentarioId, payload);
      notify("Mensagem atualizada.", "success");
    } catch (falha) {
      const { mensagem } = falha as { mensagem?: string };
      notify(mensagem ?? "Não foi possível salvar a edição.", "error", 9000);
      throw falha;
    }
  };

  return (
    <Box sx={styles.page}>
      <Paper sx={styles.header}>
        <Box
          sx={{
            ...styles.headerGlow,
            width: 190,
            height: 190,
            top: -105,
            right: -30,
          }}
        />
        <Box
          sx={{
            ...styles.headerGlow,
            width: 110,
            height: 110,
            bottom: -70,
            right: 140,
          }}
        />

        <Box sx={styles.headerTop}>
          <Box sx={styles.headerIdent}>
            <Box sx={styles.headerIcon}>
              <SupportAgentOutlinedIcon sx={{ fontSize: 26 }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={styles.headerTitulo}>Meus chamados</Typography>
              <Typography sx={styles.headerSubtitulo}>
                Acompanhe suas solicitações e converse com a equipe de suporte.
              </Typography>
            </Box>
          </Box>

          {!somenteLeitura && (
            <Box sx={styles.headerAcao}>
              <Box sx={styles.headerDivisor} />
              <Button
                variant="contained"
                disableElevation
                startIcon={<AddRoundedIcon />}
                onClick={() => setModalAberto(true)}
                sx={styles.headerCta}
              >
                Novo chamado
              </Button>
            </Box>
          )}
        </Box>
      </Paper>

      {erro && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {erro.mensagem}
        </Alert>
      )}

      {!erro && !online && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={handleSincronizar}
              disabled={sincronizando}
            >
              {sincronizando ? "Sincronizando…" : "Tentar novamente"}
            </Button>
          }
        >
          Sem conexão.{" "}
          {sincronizadoEm
            ? `Última atualização em ${dayjs(sincronizadoEm).format("DD/MM [às] HH:mm")}.`
            : (aviso ?? "Aguardando primeira sincronização.")}
        </Alert>
      )}

      {somenteLeitura && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Este canal é somente leitura. Abrir e comentar continua pelo canal de
          sempre.
        </Alert>
      )}

      <Paper sx={styles.barra}>
        <Box sx={styles.tabsRow}>
          <Box sx={styles.segmented}>
            {ABAS.map(({ chave, rotulo }) => {
              const ativa = aba === chave;
              return (
                <ButtonBase
                  key={chave}
                  onClick={() => trocarAba(chave)}
                  sx={styles.segmentedItem(ativa)}
                  aria-pressed={ativa}
                >
                  {rotulo}
                  <Box component="span" sx={styles.segmentedContagem(ativa)}>
                    {totalPorAba[chave]}
                  </Box>
                </ButtonBase>
              );
            })}
          </Box>

          {!somenteLeitura && (
            <Tooltip
              title={
                aba === "arquivadas"
                  ? "Voltar para os chamados abertos"
                  : "Ver chamados arquivados"
              }
              arrow
            >
              <ButtonBase
                onClick={() =>
                  trocarAba(aba === "arquivadas" ? "abertas" : "arquivadas")
                }
                sx={styles.arquivoBotao(aba === "arquivadas")}
                aria-pressed={aba === "arquivadas"}
                aria-label="Chamados arquivados"
              >
                <Inventory2OutlinedIcon sx={{ fontSize: 16 }} />
                Arquivadas
              </ButtonBase>
            </Tooltip>
          )}
        </Box>

        <Box sx={styles.filtrosDivisor} />

        <Box sx={styles.filtrosRow}>
          <FiltersBar
            busca={busca}
            onBusca={setBusca}
            status={status}
            onStatus={setStatus}
            opcoesStatus={aba === "arquivadas" ? STATUS_LIST : STATUS_ABERTAS}
            mostrarStatus={aba !== "concluidas"}
            periodo={periodo}
            onPeriodo={setPeriodo}
            ativos={ativos}
            onLimpar={limparFiltros}
          />
        </Box>
      </Paper>

      <Fade in key={aba} timeout={260}>
        <Box>
          {!loading && lista.length === 0 ? (
            <Paper sx={styles.tabelaPaper}>
              <EmptyState
                titulo={
                  ativos > 0
                    ? "Nenhum chamado com esses filtros"
                    : VAZIO[aba].titulo
                }
                texto={
                  ativos > 0
                    ? "Ajuste a busca, o status ou o período para ver mais resultados."
                    : VAZIO[aba].texto
                }
                acao={
                  ativos > 0
                    ? { label: "Limpar filtros", onClick: limparFiltros }
                    : undefined
                }
              />
            </Paper>
          ) : (
            <DemandasTable
              demandas={lista}
              loading={loading}
              resetKey={`${aba}|${buscaAdiada}|${status
                .map((meta) => meta.key)
                .join(",")}|${periodo.inicio}|${periodo.fim}`}
              onAbrir={(demanda) => abrirDemanda(demanda.codigo)}
              onArquivar={somenteLeitura ? undefined : setDemandaParaArquivar}
              arquivandoId={arquivandoId}
            />
          )}
        </Box>
      </Fade>

      <DemandaDrawer
        demanda={aberta}
        usuarioId={usuario.id}
        somenteLeitura={somenteLeitura}
        onClose={fecharDemanda}
        onTentarNovamente={handleTentarNovamente}
        onAdicionarComentario={handleComentar}
        onResponder={handleResponder}
        onEditarComentario={handleEditar}
        onArquivar={somenteLeitura ? undefined : setDemandaParaArquivar}
        arquivando={!!aberta && arquivandoId === aberta.id}
      />

      <NovaDemandaModal
        open={modalAberto}
        onClose={() => setModalAberto(false)}
        onCriar={handleCriar}
      />

      <ArquivarModal
        demanda={demandaParaArquivar}
        onClose={() => setDemandaParaArquivar(null)}
        onConfirmar={confirmarArquivar}
      />
    </Box>
  );
}
